import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { FiLock, FiEye, FiEyeOff } from 'react-icons/fi'
import { Container, Button, Input, Heading1, BodyText } from '../components/ui'
import { theme, mediaQuery } from '../theme'
import { authAPI } from '../services/api'

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%);
  padding: ${theme.spacing.lg};
`

const LoginCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.hover};
  padding: ${theme.spacing.xxl};
  width: 100%;
  max-width: 400px;
  text-align: center;
`

const Logo = styled.div`
  width: 80px;
  height: 80px;
  background: ${theme.colors.primary.main};
  border-radius: ${theme.borderRadius.round};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.lg};
  
  svg {
    font-size: 40px;
    color: white;
  }
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  margin-top: ${theme.spacing.lg};
`

const InputContainer = styled.div`
  position: relative;
`

const PasswordToggle = styled.button`
  position: absolute;
  right: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.primary.light};
  cursor: pointer;
  font-size: 18px;
  
  &:hover {
    color: ${theme.colors.primary.main};
  }
`

const ErrorMessage = styled.div`
  background: ${theme.colors.status.error};
  color: white;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.body.p2.fontSize};
  margin-bottom: ${theme.spacing.md};
`

const LoginPage = () => {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)
    
    try {
      await authAPI.login(password)
      navigate('/dashboard')
    } catch (err) {
      setError('Invalid password. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }
  
  return (
    <LoginContainer>
      <LoginCard>
        <Logo>
          <FiLock />
        </Logo>
        
        <Heading1 style={{ marginBottom: theme.spacing.md, fontSize: '30px' }}>
          Dory Analytics
        </Heading1>
        
        <BodyText style={{ marginBottom: theme.spacing.lg, color: theme.colors.primary.light }}>
          Mental Resilience Dashboard
        </BodyText>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <Form onSubmit={handleSubmit}>
          <InputContainer>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
            />
            <PasswordToggle 
              type="button" 
              onClick={togglePasswordVisibility}
              disabled={isLoading}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </PasswordToggle>
          </InputContainer>
          
          <Button 
            type="submit" 
            fullWidth 
            disabled={isLoading || !password.trim()}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </Form>
      </LoginCard>
    </LoginContainer>
  )
}

export default LoginPage 